import {DtoEnumInfo} from 'features/core/shared/dto/dto-enum-info.interface';
import {ProvisionTransactionEvent} from './provision-transaction-event.enum';
import {ProvisionAccountKind} from './provision-account-kind.enum';
import {CP_TRANSACTION_EVENT_LABELS} from './provision-transaction-event-label';
import {CP_ACCOUNT_KIND_LABELS} from './provision-account-kind-label';
import {provisionEnumLabelFallback} from './provision-enum-label-fallback';

/**
 * German captions for TECHNICAL CONSTANT NAMES — the DEPLOY-WINDOW fallback of the simulation
 * screen, no longer its primary source, and the deliberate rule that anything not listed here is
 * rendered VERBATIM.
 *
 * ── THE INFO BLOCKS ARE THE PRIMARY SOURCE NOW
 *
 * Every enum-valued field of the simulation response carries a sibling `…Info: DtoEnumInfo` with the
 * German label, icon and colours straight from the backend `@UiEnumValueInfo`. `stateInfo`,
 * `kindInfo` and `verdictInfo` always did; `agentRoleInfo`, `calculationModeInfo`,
 * `liabilitySourceInfo`, `liabilityStatusInfo`, `eventInfo`, `accountKindInfo`, `severityInfo` and
 * `axisInfo` were added when the seven Satzkopf fields and the candidate axis were typed as enums.
 * A caption is therefore read off `…Info.label` FIRST, and that is the only source that stays
 * correct when the backend appends a constant nobody has written a German word for on this side.
 *
 * ── WHAT IS LEFT FOR THIS FILE
 *
 * The window between the two deploys. This frontend ships by CI and the backend by hand, so a
 * browser running the new code can talk to a server that still sends `"MONTHLY_PREMIUM"` and no
 * `calculationModeInfo` at all. In that window the map below is what stands between the reader and
 * a screen printing `MONTHLY_PREMIUM` at them in a German commission protocol.
 *
 * It also still serves the protocol's `figures[]`, whose values are plain strings by design and
 * occasionally a constant name: measured 2026-09-02, `Berechnungsart = "MONTHLY_PREMIUM"`,
 * `Modus = "FULL"`, `Preisreferenz = "PAYMENT"`, `Status = "ACTIVE"`, `Bezahlte Rolle = "main"`.
 * That was reported and fixed at the source — re-measured the same day the figures arrive resolved
 * — but a figure is a free-form key/value pair with no `Info` block to fall back on, so an older
 * backend or a newly added figure lands here again.
 *
 * Cost of keeping it: nothing. The map is a constant.
 *
 * ── WHY A MAP AND NOT A TRANSLATION
 *
 * This file translates NOTHING it was not given a measured backend caption for. Every entry below is
 * copied VERBATIM out of the `@UiEnumValueInfo(uiLabel = …)` of the backend enum that produces the
 * constant — read from the enum sources, never paraphrased — so the same constant reads identically
 * here and on every other screen of the application.
 *
 * ── ONE CAPTION, ONE OWNER
 *
 * The booking events and the two account kinds are NOT typed out again below. They are re-keyed from
 * {@link CP_TRANSACTION_EVENT_LABELS} and {@link CP_ACCOUNT_KIND_LABELS}, the maps this module
 * already owns for the account statement, by {@link byConstantName}. They were copied here once and
 * the copy is exactly the kind of thing that survives a wording change on one side only: two screens
 * of the same module would then name the same booking differently, and nothing would report it.
 * A new booking event now needs a caption in ONE place.
 *
 * ── AN UNKNOWN CONSTANT STAYS VISIBLE, NOT BLANK
 *
 * Appending constants to a backend enum is routine, and it reaches this file as a name nobody wrote
 * a caption for. {@link provisionSimulationTechnicalLabel} then returns the name UNCHANGED — ugly
 * on purpose, because on a screen that explains money a blank cell reads as "there is nothing here"
 * while an unmapped `SOME_NEW_CONSTANT` reads as "this screen is one release behind". The same
 * reasoning as `provisionEnumLabelFallback`, one step further: here the raw name is genuinely
 * informative, so it is the fallback itself.
 *
 * ── WHAT THIS FILE MUST NEVER BECOME
 *
 * A place to invent business wording. If a new constant needs a German caption, the caption belongs
 * on the backend enum as `@UiEnumValueInfo(uiLabel = …)` — that is where every other cp surface
 * reads it from — and the better fix is for the endpoint to send the resolved label beside the
 * value, the way the `…Info` blocks do. That is exactly the route the figures and the seven Satzkopf
 * fields took, and it is why this map matters less every release.
 */

/**
 * Re-keys an ORDINAL-keyed label map of a numeric enum by the CONSTANT NAMES.
 *
 * The two source maps are keyed by the enum member, which in a numeric TypeScript enum means they
 * are physically keyed by the ordinal — `{0: 'Provisionsbuchung', …}`. What arrives on the wire in
 * the deploy window is the NAME, so the ordinals are translated back through the enum object's
 * reverse mapping, the same idiom `provisionEnumLabelFallback` uses.
 */
function byConstantName(enumObject: object,
                        labels: Readonly<Record<number, string>>): Record<string, string> {

    const byName: Record<string, string> = {};

    for (const ordinal of Object.keys(labels)) {

        const name: unknown = (enumObject as Record<number, unknown>)[Number(ordinal)];

        if (typeof name === 'string') {
            byName[name] = labels[Number(ordinal)];
        }
    }

    return byName;
}

const CP_SIMULATION_TECHNICAL_LABELS: Readonly<Record<string, string>> = {

    // cp.enums.ProvisionMatrixCalcMode — how the matrix factor is applied
    MONTHLY_PREMIUM: 'Monatsbeiträge',
    PER_MILLE: 'Promille',

    // cp.enums.ProvisionBaseCalculationMode — how the base amount is derived
    FULL: 'Voller Betrag',
    DIFFERENCE: 'Differenz',

    // cp.enums.ProvisionPriceReference — which price column of the contract is the base
    GROSS: 'Bruttobeitrag',
    NET: 'Nettobeitrag',
    PAYMENT: 'Zahlbeitrag',
    LOGIC: 'Eigene Berechnung',
    GROSS_NET_OF_TAX: 'Brutto netto (ohne Steuer, ohne Gebühr)',
    PAYMENT_NET_OF_TAX: 'Zahlbeitrag netto (ohne Steuer, ohne Gebühr)',

    // cp.enums.ProvisionBaseContractReference — which contract's price row is the base
    OWN: 'Eigener Vertrag',
    FORMER: 'Vorvertrag',

    // cp.enums.ProvisionLiabilitySource — which carrier decided the liability type.
    // THREE constants, not two: the row overwrite (K-09) beats the assignment overwrite (K-10),
    // which beats the liability assignment (K-07). MATRIX_ROW_OVERWRITE is the one the reference
    // tenant produces most (49 % of its rows), so a map without it prints the raw name at the
    // reader — measured on 2026-09-02 before this line existed.
    ASSIGNMENT: 'Zuordnung',
    MATRIX_OVERWRITE: 'Matrix-Überschreibung',
    MATRIX_ROW_OVERWRITE: 'Matrixzeilen-Überschreibung',

    // cp.enums.ProvisionLiabilityStatus — state of the liability of one entitlement
    ACTIVE: 'Aktiv',
    EXPIRED: 'Abgelaufen',
    CANCELLED: 'Storniert',
    SUSPENDED: 'Ausgesetzt',

    // cp.enums.ProvisionMessageSeverity — how bad a message of the calculation chain is.
    // The one enum of the response whose constants read as English words a German screen would
    // otherwise print unchanged: "ERROR" beside a commission amount is not a caption, and unlike
    // MONTHLY_PREMIUM it does not even look wrong enough to be reported.
    INFO: 'Information',
    WARNING: 'Warnung',
    ERROR: 'Fehler',

    // cm.enums.ContractAgentEnum / cp.enums.ProvisionAgentRoleEnum — the paid role
    main: 'abgeschlossen durch',
    support: 'unterstützt durch',
    inquired: 'eingeholt durch',
    calculated: 'berechnet',

    // cp.enums.ProvisionAccountKind — which sub-account a booking hits. Owned by
    // provision-account-kind-label.ts; re-keyed, never re-typed.
    ...byConstantName(ProvisionAccountKind, CP_ACCOUNT_KIND_LABELS),

    // cp.enums.ProvisionTransactionEvent — the engine event behind a booking. Owned by
    // provision-transaction-event-label.ts; re-keyed, never re-typed.
    ...byConstantName(ProvisionTransactionEvent, CP_TRANSACTION_EVENT_LABELS),

    // cp.enums.ProvisionConfigurationAxis — the axis a K-09 matrix row was configured on. The
    // wording is the one the Beratersicht already prints for the same distinction.
    AGENT: 'Persönlich',
    GROUP: 'Gruppe'
};

/**
 * German caption of a technical constant NAME, or the name itself when none was written for it.
 *
 * A genuinely absent value (`null` / `undefined`) is a different statement from an unmapped one and
 * yields an empty string: the response has no such attribute, which is not a mapping gap.
 */
export function provisionSimulationTechnicalLabel(value: string): string {

    if (value == null || value === '') {
        return '';
    }

    return CP_SIMULATION_TECHNICAL_LABELS[value] ?? value;
}

/**
 * The ONE reading rule for an enum-valued field of the simulation response — Info block first, then
 * the deploy-window fallbacks, in that order and never in another.
 *
 * ── WHY THIS IS A FUNCTION AND NOT A COMMENT
 *
 * Eight fields of the response now arrive as `<value> + <value>Info`, and each of them can reach the
 * browser in three shapes: the ordinal with its Info block (what a current backend sends), the
 * ordinal alone (a constant appended after this release), or the bare constant NAME with no Info at
 * all (a backend older than the change — frontend ships by CI, backend by hand, so that window is
 * real). Written out at every call site, the three-way branch is eight chances to get the order
 * wrong, and getting it wrong is invisible: reading the raw value first still produces a string, it
 * is just the wrong one, and reading the map first prints `0` for an ordinal the map cannot know.
 *
 * The order below is the whole content of this function:
 *
 *   1. `info.label` — resolved by the backend from its own `@UiEnumValueInfo`, correct for every
 *      constant including ones this release has never heard of. Always preferred.
 *   2. a NAME — the deploy window; {@link provisionSimulationTechnicalLabel} captions it, or hands
 *      it back verbatim, which stays visible rather than blank.
 *   3. an ORDINAL without an Info block — `provisionEnumLabelFallback` turns it into the enum KEY,
 *      which is then run through the caption map like any other name, so a known constant still
 *      reads German. When even the key is unknown the fallback yields `#<ordinal>`, which the map
 *      hands back unchanged. Loud on purpose: ugly is visible, visible is fixed, and a blank cell
 *      on a screen that explains money reads as "there is nothing here".
 *
 * An absent value yields an empty string: the row has no such attribute, which is a different
 * statement from an unmapped one and must not be dressed up as a gap.
 *
 * @param info       the sibling `…Info` block, when the backend sent one
 * @param value      the field value — ordinal, constant name, or absent
 * @param enumObject the mirror enum of the field, for the ordinal fallback
 */
export function provisionSimulationEnumLabel(info: DtoEnumInfo,
                                             value: number | string,
                                             enumObject: object): string {

    if (info?.label) {
        return info.label;
    }

    if (value == null || value === '') {
        return '';
    }

    if (typeof value === 'string') {
        return provisionSimulationTechnicalLabel(value);
    }

    // The ordinal becomes the enum key, and the key is a constant name like any other — so it goes
    // through the same caption map instead of arriving on screen as MATRIX_OVERWRITE.
    return provisionSimulationTechnicalLabel(provisionEnumLabelFallback(enumObject, value));
}
